import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '../../../components/widgets';
import Icon, { IconVariants } from '../../../components/foundations/Icon';
import { downloadFileResponse } from '../../../global/utils';
import { buildErrorCsv } from '../../utils';
import { IMPORT_MODAL_PREFIX as PREFIX, importResultShape } from './consts';
import css from './ImportModal.css';

const SuccessBanner = ({ translate, result }) => (
    <div className={css.successBanner}>
        <Icon name="circle-check" variant={IconVariants.SUCCESS} interactive={false} />
        <div>
            <p className={css.bannerTitle}>{translate(`${PREFIX}.SUCCESS_TITLE`, { rows: result.rowsImported })}</p>
            <p className={css.bannerSub}>{translate(`${PREFIX}.SUCCESS_SUB`)}</p>
        </div>
    </div>
);

SuccessBanner.propTypes = { translate: PropTypes.func.isRequired, result: importResultShape.isRequired };

const MessageError = ({ translate, message }) => (
    <div className={css.errorBanner}>
        <Icon name="circle-x" variant={IconVariants.ERROR} interactive={false} />
        <div>
            <p className={css.bannerTitle}>{translate(`${PREFIX}.ERROR_TITLE`)}</p>
            <p className={css.messageText}>{message}</p>
        </div>
    </div>
);

MessageError.propTypes = { translate: PropTypes.func.isRequired, message: PropTypes.string.isRequired };

const ErrorReport = ({ translate, result }) => {
    const errors = result.errors || [];
    const totalErrors = result.totalErrors == null ? errors.length : result.totalErrors;

    return (
        <>
            <div className={css.errorBanner}>
                <Icon name="circle-x" variant={IconVariants.ERROR} interactive={false} />
                <div>
                    <p className={css.bannerTitle}>{translate(`${PREFIX}.ERROR_TITLE`)}</p>
                    <p className={css.bannerSub}>
                        {translate(`${PREFIX}.ERROR_SUB`, { errorRows: result.errorRows, totalRows: result.totalRows })}
                    </p>
                </div>
            </div>
            <div className={css.errorToolbar}>
                <span className={css.errorCount}>
                    {translate(`${PREFIX}.ERRORS_COUNT`, { errors: totalErrors, rows: result.errorRows })}
                </span>
                <Button
                    type="link"
                    onClick={() =>
                        downloadFileResponse({
                            contents: buildErrorCsv(errors, totalErrors),
                            filename: 'import-errors.csv',
                        })
                    }
                >
                    <Icon name="download" interactive={false} /> {translate(`${PREFIX}.DOWNLOAD_ERRORS`)}
                </Button>
            </div>
            <table className={css.errorTable}>
                <tbody>
                    <tr>
                        <th>{translate(`${PREFIX}.ERR_ROW`)}</th>
                        <th>{translate(`${PREFIX}.ERR_COLUMN`)}</th>
                        <th>{translate(`${PREFIX}.ERR_REASON`)}</th>
                    </tr>
                    {errors.map((error, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`${error.row}-${error.column}-${index}`}>
                            <td className={css.rowNum}>{error.row}</td>
                            <td className={css.errColumn}>{error.column}</td>
                            <td>{error.reason}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {errors.length < totalErrors && (
                <p className={css.truncationNote}>
                    {translate(`${PREFIX}.ERRORS_TRUNCATED`, { shown: errors.length })}
                </p>
            )}
        </>
    );
};

ErrorReport.propTypes = { translate: PropTypes.func.isRequired, result: importResultShape.isRequired };

const ResultView = ({ translate, result, onBackToUpload, onClose }) => (
    <>
        <div className={css.body}>
            {result.status === 'success' && <SuccessBanner translate={translate} result={result} />}
            {result.status === 'error' && result.message && (
                <MessageError translate={translate} message={result.message} />
            )}
            {result.status === 'error' && !result.message && <ErrorReport translate={translate} result={result} />}
        </div>
        <div className={css.footer}>
            <Button type="secondary" onClick={onBackToUpload}>
                {translate(`${PREFIX}.BACK`)}
            </Button>
            <Button type="primary" onClick={onClose}>
                {translate(`${PREFIX}.DONE`)}
            </Button>
        </div>
    </>
);

ResultView.propTypes = {
    translate: PropTypes.func.isRequired,
    result: importResultShape.isRequired,
    onBackToUpload: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ResultView;
